import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-ink-950 px-4 text-center">
          <div className="text-[42px]">!</div>
          <h1 className="mt-4 text-[18px] font-semibold text-ink-100">
            Something went wrong
          </h1>
          <p className="mt-2 max-w-[44ch] text-[13px] text-ink-400">
            Remedy hit an unexpected error. You can try recovering without a full reload, or refresh if that doesn't help.
          </p>
          {this.state.error && (
            <pre className="mono mt-4 max-w-[60ch] overflow-x-auto rounded-md border border-ink-800 bg-ink-900 px-4 py-2 text-left text-[11px] text-rose-300">
              {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="mono rounded-md border border-ink-700 bg-ink-900 px-4 py-2 text-[12px] text-ink-200 hover:border-ink-600"
            >
              Try to recover
            </button>
            <button
              onClick={() => window.location.reload()}
              className="mono rounded-md bg-accent-500 px-4 py-2 text-[12px] font-medium text-ink-950 hover:bg-accent-400"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
